// PinForm — bottom-sheet that appears after a worker taps the map.
// Pick a learner (or 全員), attach a photo, confirm. No cap on pins per
// learner — each tap adds a new pin, never replaces.

import { MonoLabel, T, TextBtn } from "@goodbye/ui";
import type { UserDoc, WithId } from "@goodbye/firebase";
import { useRef, useState } from "react";
import type { MarkerWithPins } from "../hooks/useAdminSync";
import { isRewardRegion as isRewardRegionId } from "../lib/rewardRegions";

// Sentinel for the "全員" option. Parent expands this into the full
// user-id list. Picked an unambiguous prefix that can't collide with
// any real Firestore docId.
const ALL_USERS = "__all__" as const;

export type PinKind = "reward" | "collectible" | "announcement";

export interface PinFormProps {
  pending: { lat: number; lng: number };
  /** Active region id — gates the 「藏照片？」reward checkbox (ruifang only). */
  regionId: string;
  users: WithId<UserDoc>[];
  /** Parent markers (with pins[]) in the active region. */
  regionMarkers: MarkerWithPins[];
  onConfirm: (input: {
    userIds: string[];
    clue: string;
    photoFiles: File[];
    pinKind: PinKind;
    /** Collectible-pin payload that becomes the learner's fragment. */
    payloadText: string;
    payloadPhotoFiles: File[];
    payloadVideoFile: File | null;
  }) => void;
  onCancel: () => void;
}

export function PinForm({
  regionId,
  users,
  regionMarkers,
  onConfirm,
  onCancel,
}: PinFormProps) {
  const [selectedValue, setSelectedValue] = useState<string>("");
  // 2026-05-30: multi-photo — workers can attach several clue photos at once.
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  const isRewardRegion = isRewardRegionId(regionId);

  // 2026-05-30 pin kind:瑞芳兩種互斥(預設「藏照片」reward);其他區一律
  // 「藏其他碎片」collectible(不顯示選框)。
  const [pinKind, setPinKind] = useState<PinKind>(
    isRewardRegion ? "reward" : "collectible",
  );
  // 2026-05-30「可收集碎片」勾選(預設開)。只在 collectible 路徑(非 reward)
  // 顯示。取消勾 → 這個點變「公告」(announcement):學員看得到內容、沒有收集
  // 按鈕,用來放集合地點之類的公告。
  const [collectEnabled, setCollectEnabled] = useState(true);
  // 有效 kind:reward 不受影響;collectible 路徑下,勾選開=collectible、
  // 關=announcement。
  const effectiveKind: PinKind =
    pinKind === "reward"
      ? "reward"
      : collectEnabled
        ? "collectible"
        : "announcement";
  // payload 輸入(文字/照片/影片)在 collectible 跟 announcement 都顯示
  // (公告也能附內容),只有 reward 不顯示。
  const isCollectible = effectiveKind === "collectible" || effectiveKind === "announcement";

  // 「藏其他碎片」payload:文字 / 多照片 / 單影片。學員收集後進其碎片。
  const [payloadText, setPayloadText] = useState<string>("");
  const [payloadPhotoFiles, setPayloadPhotoFiles] = useState<File[]>([]);
  const [payloadVideoFile, setPayloadVideoFile] = useState<File | null>(null);
  const payloadPhotoInputRef = useRef<HTMLInputElement | null>(null);
  const payloadVideoInputRef = useRef<HTMLInputElement | null>(null);

  const pinCountByUser = new Map<string, number>(
    regionMarkers.map((m) => [m.userId, m.pins.length]),
  );

  const isAll = selectedValue === ALL_USERS;
  const selectedUserId = isAll ? "" : selectedValue;
  const selectedCount = selectedUserId
    ? pinCountByUser.get(selectedUserId) ?? 0
    : 0;

  const ready = selectedValue !== "";

  function resetForm() {
    setSelectedValue("");
    setPhotoFiles([]);
    setPinKind(isRewardRegion ? "reward" : "collectible");
    setCollectEnabled(true);
    setPayloadText("");
    setPayloadPhotoFiles([]);
    setPayloadVideoFile(null);
  }

  function handleConfirm() {
    if (!ready) return;
    const userIds = isAll ? users.map((u) => u.id) : [selectedValue];
    // 有效 kind:瑞芳可選 reward;否則 collectible / announcement(由「可收集
    // 碎片」勾選決定)。announcement 也帶 payload(公告的文字/照片),只是學員端
    // 不顯示收集按鈕。
    const kind = effectiveKind;
    const hasPayload = kind === "collectible" || kind === "announcement";
    onConfirm({
      userIds,
      clue: "",
      photoFiles,
      pinKind: kind,
      payloadText: hasPayload ? payloadText.trim() : "",
      payloadPhotoFiles: hasPayload ? payloadPhotoFiles : [],
      payloadVideoFile: hasPayload ? payloadVideoFile : null,
    });
    resetForm();
  }

  function handleCancel() {
    resetForm();
    onCancel();
  }

  function openPhotoPicker() {
    photoInputRef.current?.click();
  }

  function onPhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    // Append to whatever's already picked so workers can add in batches.
    if (picked.length > 0) setPhotoFiles((prev) => [...prev, ...picked]);
    // Reset the input so re-picking the same file fires onChange again.
    e.target.value = "";
  }

  function removePhotoAt(idx: number) {
    setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  function onPayloadPhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    if (picked.length > 0) setPayloadPhotoFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  }
  function removePayloadPhotoAt(idx: number) {
    setPayloadPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
  }
  function onPayloadVideoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (f && f.size > 300 * 1024 * 1024) {
      // 跟 storage rule / FragmentNew 一致的 300MB 上限。
      window.alert("影片超過 300MB、請先在手機上剪短或選短一點的影片。");
      e.target.value = "";
      return;
    }
    setPayloadVideoFile(f);
    e.target.value = "";
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
        }}
      >
        <MonoLabel size={12} color={T.ink900}>
          {isAll
            ? `全員 ${users.length} 位 · 各加一個`
            : !selectedUserId
              ? "選 學 員"
              : // 「藏照片」(reward)顯示「藏第幾張圖」;「藏其他碎片」顯示
                // 學員名 +「藏碎片」。
                isRewardRegion && pinKind === "reward"
                ? `藏第 ${selectedCount + 1} 張圖`
                : `${users.find((u) => u.id === selectedUserId)?.name ?? selectedUserId}${isCollectible ? " · 藏碎片" : ""}`}
        </MonoLabel>
        <TextBtn
          size={12}
          color={T.ink350}
          onClick={handleCancel}
          framed={false}
          style={{ padding: "4px 6px" }}
        >
          · 取消
        </TextBtn>
      </div>

      <div>
        <MonoLabel
          size={11}
          color={T.ink500}
          style={{ display: "block", marginBottom: 6 }}
        >
          指派給 ({users.length} 位)
        </MonoLabel>
        <select
          value={selectedValue}
          onChange={(e) => setSelectedValue(e.target.value)}
          style={{
            width: "100%",
            background: T.paper,
            border: `1px solid ${T.ink200}`,
            padding: "10px 12px",
            fontFamily: T.han,
            fontWeight: 300,
            fontSize: 15,
            color: T.ink900,
            outline: "none",
            borderRadius: 0,
            appearance: "none",
            WebkitAppearance: "none",
            cursor: "pointer",
          }}
        >
          <option value="">— 選一位學員 —</option>
          <option value={ALL_USERS}>全員 · 一次替每位都藏一個</option>
          {users.map((u) => {
            const count = pinCountByUser.get(u.id) ?? 0;
            return (
              <option key={u.id} value={u.id}>
                {u.no} · {u.name} (已 {count} 個)
              </option>
            );
          })}
        </select>
      </div>

      {/* Day 14: text-clue input removed — only photo is used as visual
          hint. Workers point the camera at the hidden item (or pick from
          library if they already shot it) and the learner sees it in the
          map popover when arrived. 2026-05-30: 標題移除,說明做在按鈕上
          (「加提示照片」),按鈕加框成明確按鈕。所有 pin 種類都可附。 */}
      <div>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onPhotoChange}
          style={{ display: "none" }}
        />
        {photoFiles.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 8,
            }}
          >
            {photoFiles.map((f, idx) => (
              <div
                key={`${f.name}-${idx}`}
                style={{ position: "relative", width: 64, height: 64 }}
              >
                <img
                  src={URL.createObjectURL(f)}
                  alt={`預覽 ${idx + 1}`}
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: "cover",
                    border: `1px solid ${T.ink200}`,
                    display: "block",
                  }}
                />
                <button
                  type="button"
                  onClick={() => removePhotoAt(idx)}
                  aria-label={`移除第 ${idx + 1} 張`}
                  style={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    width: 30,
                    height: 30,
                    border: `1px solid ${T.ink900}`,
                    background: T.paper,
                    color: T.ink900,
                    fontFamily: T.mono,
                    fontSize: 18,
                    lineHeight: 1,
                    cursor: "pointer",
                    borderRadius: 0,
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    touchAction: "manipulation",
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        <TextBtn
          size={13}
          color={T.ink900}
          mono={false}
          onClick={openPhotoPicker}
          framed
          style={{
            padding: "10px 16px",
            border: `1px solid ${T.ink900}`,
            alignSelf: "flex-start",
          }}
        >
          {photoFiles.length > 0 ? "再加提示照片" : "加提示照片"}
        </TextBtn>
      </div>

      {/* Pin 種類 — 只在瑞芳顯示兩個互斥選項(預設「藏照片」);其他區
          一律「藏其他碎片」、不顯示選框。 */}
      {isRewardRegion && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <MonoLabel size={11} color={T.ink500}>· 這 個 點 是</MonoLabel>
          {(
            [
              ["reward", "瑞芳市區藏照片"],
              ["collectible", "藏其他碎片(附照片/影片/文字)"],
            ] as const
          ).map(([kind, label]) => (
            <label
              key={kind}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "4px 4px",
                cursor: "pointer",
                minHeight: 30,
                fontFamily: T.han,
                fontSize: 13,
                color: T.ink900,
              }}
            >
              <input
                type="radio"
                name="pin-kind"
                checked={pinKind === kind}
                onChange={() => setPinKind(kind)}
                style={{ width: 16, height: 16, margin: 0, accentColor: "#0E0E0E" }}
              />
              {label}
            </label>
          ))}
        </div>
      )}

      {/* 2026-05-30「可收集碎片」勾選(預設開)。只在非 reward 路徑顯示。取消勾
          → 這個點變「公告」:學員看得到內容、靠近不會出現收集按鈕(放集合地點
          之類)。瑞芳選 reward 時不顯示此勾選。 */}
      {pinKind !== "reward" && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "4px 4px",
            cursor: "pointer",
            minHeight: 30,
            fontFamily: T.han,
            fontSize: 13,
            color: T.ink900,
          }}
        >
          <input
            type="checkbox"
            checked={collectEnabled}
            onChange={(e) => setCollectEnabled(e.target.checked)}
            style={{ width: 16, height: 16, margin: 0, accentColor: "#0E0E0E" }}
          />
          可收集碎片
        </label>
      )}
      {pinKind !== "reward" && !collectEnabled && (
        <MonoLabel size={10} color={T.ink500}>
          · 公 告 模 式:學 員 看 得 到 內 容、但 不 會 出 現 收 集 按 鈕
        </MonoLabel>
      )}

      {/* payload — 文字 / 多照片 / 單影片。collectible 是學員收集後的碎片;
          announcement(公告)則是地圖上顯示的內容。兩者都顯示這些欄位。 */}
      {isCollectible && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            paddingTop: 2,
            borderTop: `1px solid ${T.ink100}`,
          }}
        >
          <MonoLabel size={11} color={T.ink500}>· 碎 片 內 容(學員收集後會拿到)</MonoLabel>
          {/* 文字 — 單行起、可往下拉。降低預設高度。 */}
          <textarea
            value={payloadText}
            onChange={(e) => setPayloadText(e.target.value)}
            placeholder="想對學員說的話(可留空)"
            rows={1}
            style={{
              width: "100%",
              background: T.paper,
              border: `1px solid ${T.ink200}`,
              padding: "6px 10px",
              fontFamily: T.han,
              fontWeight: 300,
              fontSize: 14,
              color: T.ink900,
              outline: "none",
              borderRadius: 0,
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
          {/* 碎片照片(多張) */}
          <input
            ref={payloadPhotoInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onPayloadPhotoChange}
            style={{ display: "none" }}
          />
          {payloadPhotoFiles.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {payloadPhotoFiles.map((f, idx) => (
                <div
                  key={`${f.name}-${idx}`}
                  style={{ position: "relative", width: 52, height: 52 }}
                >
                  <img
                    src={URL.createObjectURL(f)}
                    alt={`碎片照 ${idx + 1}`}
                    style={{
                      width: 52,
                      height: 52,
                      objectFit: "cover",
                      border: `1px solid ${T.ink200}`,
                      display: "block",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removePayloadPhotoAt(idx)}
                    aria-label={`移除碎片照 ${idx + 1}`}
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      width: 30,
                      height: 30,
                      border: `1px solid ${T.ink900}`,
                      background: T.paper,
                      color: T.ink900,
                      fontFamily: T.mono,
                      fontSize: 18,
                      lineHeight: 1,
                      cursor: "pointer",
                      borderRadius: 0,
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      touchAction: "manipulation",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* 碎片影片(單支,≤300MB,放點時轉檔) */}
          <input
            ref={payloadVideoInputRef}
            type="file"
            accept="video/*"
            onChange={onPayloadVideoChange}
            style={{ display: "none" }}
          />
          {/* 加照片 + 加影片並排一列,省高度 */}
          <div style={{ display: "flex", gap: 16, alignItems: "baseline", flexWrap: "wrap" }}>
            <TextBtn
              size={12}
              color={T.ink700}
              mono={false}
              onClick={() => payloadPhotoInputRef.current?.click()}
              framed={false}
              style={{ padding: "4px 0" }}
            >
              {payloadPhotoFiles.length > 0 ? "· 再加照片" : "· 加照片"}
            </TextBtn>
            {!payloadVideoFile && (
              <TextBtn
                size={12}
                color={T.ink700}
                mono={false}
                onClick={() => payloadVideoInputRef.current?.click()}
                framed={false}
                style={{ padding: "4px 0" }}
              >
                · 加影片(≤300MB)
              </TextBtn>
            )}
          </div>
          {payloadVideoFile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontFamily: T.han,
                fontSize: 13,
                color: T.ink900,
              }}
            >
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                · 影片:{payloadVideoFile.name}
              </span>
              <TextBtn
                size={11}
                color={T.ink350}
                onClick={() => setPayloadVideoFile(null)}
                framed={false}
                style={{ padding: "4px 6px" }}
              >
                · 移除
              </TextBtn>
            </div>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginTop: 4,
        }}
      >
        <span style={{ fontFamily: T.han, fontSize: 13, color: T.ink350 }}>
          {/* No "還沒選學員" placeholder — the disabled 確認 button already
              signals "pick a learner first". Trimmed 2026-05-30 to shorten
              the form so it covers less of the map. */}
          {!ready
            ? ""
            : isAll
              ? `全員 ${users.length} 位 · 各加一個`
              : `指派給 ${
                  users.find((u) => u.id === selectedUserId)?.name ?? selectedUserId
                } 的第 ${selectedCount + 1} 個`}
        </span>
        <TextBtn
          onClick={handleConfirm}
          color={ready ? T.ink900 : T.ink350}
          disabled={!ready}
        >
          · 確 認
        </TextBtn>
      </div>
    </div>
  );
}
